import React, { useCallback, useEffect, useState } from "react";
import { axiosPost } from "../../../utils/AxiosApi";
import { checkWidgetAdminOrNot, URL } from "../../../utils/Constants";
import DataTable from "react-data-table-component";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChalkboardTeacher,
  faBook,
  faDoorOpen,
  faLayerGroup,
} from "@fortawesome/free-solid-svg-icons";
import GranularPermissionModal from "../../Permissions/GranularPermissionModal";
import ModalWindow from "../../UI/ModalWindow";

// ─── Band pill ────────────────────────────────────────────────────────────────
const BandPill = ({ band }) => {
  const map = {
    Excellent: { bg: "#EAF3DE", color: "#3B6D11" },
    Good: { bg: "#E6F1FB", color: "#0C447C" },
    "Needs Improvement": { bg: "#FAEEDA", color: "#633806" },
    Critical: { bg: "#FCEBEB", color: "#791F1F" },
  };
  const s = map[band] || { bg: "#f1f5f9", color: "#64748b" };
  return (
    <span
      style={{
        background: s.bg,
        color: s.color,
        padding: "2px 9px",
        borderRadius: 99,
        fontSize: 11,
        fontWeight: 600,
        whiteSpace: "nowrap",
      }}
    >
      {band || "—"}
    </span>
  );
};

// ─── Mini progress bar ────────────────────────────────────────────────────────
const MiniBar = ({ value }) => {
  const pct = Math.min(Math.max(Number(value) || 0, 0), 100);
  const color =
    pct >= 80 ? "#3B6D11" : pct >= 65 ? "#185FA5" : pct >= 45 ? "#BA7517" : "#A32D2D";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 90 }}>
      <div
        style={{
          flex: 1,
          height: 5,
          borderRadius: 3,
          background: "#e2e8f0",
          overflow: "hidden",
        }}
      >
        <div
          style={{ width: `${pct}%`, height: "100%", background: color, borderRadius: 3 }}
        />
      </div>
      <span style={{ fontSize: 12, color: "#64748b", minWidth: 36, textAlign: "right" }}>
        {pct.toFixed(1)}%
      </span>
    </div>
  );
};

// ─── Score range cell ─────────────────────────────────────────────────────────
const ScoreRange = ({ best, poorest }) => (
  <div style={{ fontSize: 11, display: "flex", gap: 5, alignItems: "center" }}>
    <span style={{ color: "#3B6D11", fontWeight: 600 }}>↑{Number(best).toFixed(1)}%</span>
    <span style={{ color: "#94a3b8" }}>·</span>
    <span style={{ color: "#A32D2D", fontWeight: 600 }}>↓{Number(poorest).toFixed(1)}%</span>
  </div>
);

// ─── Tab config ───────────────────────────────────────────────────────────────
const TABS = [
  { value: "DETAIL", label: "Detail", icon: faLayerGroup },
  { value: "TEACHER", label: "Per teacher", icon: faChalkboardTeacher },
  { value: "SUBJECT", label: "Per subject", icon: faBook },
  { value: "CLASS", label: "Per class", icon: faDoorOpen },
  { value: "SECTION", label: "Per Section", icon: faDoorOpen },
];

// ─── Column definitions ───────────────────────────────────────────────────────
const buildColumns = (groupBy) => {
  const isDetail = groupBy === "DETAIL";
  const isTeacher = groupBy === "TEACHER";
  const isSubject = groupBy === "SUBJECT";
  const isClass = groupBy === "CLASS";
  const isSection = groupBy === "SECTION";

  const all = [
    // ── Identity ──────────────────────────────────────────────────────
    {
      id: "name",
      name: "Teacher",
      selector: (r) => r.pplName,
      sortable: true,
      minWidth: "150px",
      omit: isSubject || isClass || isSection,
      cell: (r) => (
        <div>
          <div style={{ fontWeight: 600, fontSize: 13 }}>{r.pplName}</div>
          {r.pplEmail && (
            <div style={{ fontSize: 11, color: "#94a3b8" }}>{r.pplEmail}</div>
          )}
        </div>
      ),
    },
    {
      id: "subject",
      name: "Subject",
      selector: (r) => r.subjectName,
      sortable: true,
      omit: isTeacher || isClass || isSection,
      cell: (r) => (
        <span style={{ fontSize: 13 }}>
          {r.subjectName || <span style={{ color: "#94a3b8" }}>Multiple</span>}
        </span>
      ),
    },
    {
      id: "class",
      name: "Class",
      selector: (r) => isClass ? r.yearLevelId : r.classroomId,
      sortable: true,
      omit: isTeacher || isSubject,
      cell: (r) =>
        !!r.yearLevelId ? (
          <div>
            <div style={{ fontWeight: 500, fontSize: 13 }}>{r.yearLevelName}</div>
            <div style={{ fontSize: 11, color: "#94a3b8" }}>{r.classroomName}</div>
          </div>
        ) : !!r.classroomId? (
          <div>
              <div style={{ fontWeight: 500, fontSize: 13 }}>{r.classroomName}</div>
              <div style={{ fontSize: 11, color: "#94a3b8" }}>{r.yearLevelName}</div>
          </div>
        ) : (
          <span style={{ color: "#94a3b8", fontSize: 12 }}>Multiple</span>
        )
    },
    // Context counts for aggregated views
    {
      id: "scope",
      name: "Scope",
      sortable: false,
      omit: isDetail,
      cell: (r) => (
        <div style={{ fontSize: 11, color: "#64748b", lineHeight: 1.6 }}>
          {!isTeacher && r.distinctTeacherCount > 0 && (
            <div>{r.distinctTeacherCount} teacher{r.distinctTeacherCount > 1 ? "s" : ""}</div>
          )}
          {!isSubject && r.distinctSubjectCount > 0 && (
            <div>{r.distinctSubjectCount} subject{r.distinctSubjectCount > 1 ? "s" : ""}</div>
          )}
          {!isClass && r.distinctClassCount > 0 && (
            <div>{r.distinctClassCount} class{r.distinctClassCount > 1 ? "es" : ""}</div>
          )}
          {!isSection && r.distinctSectionCount > 0 && (
            <div>{r.distinctSectionCount} section{r.distinctSectionCount > 1 ? "s" : ""}</div>
          )}
        </div>
      ),
    },
    // ── Assessment & Student counts ───────────────────────────────────
    {
      id: "assessments",
      name: "Assessments",
      selector: (r) => r.totalAssessmentCount,
      sortable: true,
      center: true,
      width: "110px",
      cell: (r) => (
        <span
          style={{
            background: "#f1f5f9",
            color: "#475569",
            borderRadius: 6,
            padding: "2px 8px",
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          {r.totalAssessmentCount}
        </span>
      ),
    },
    {
      id: "students",
      name: "Students",
      selector: (r) => r.totalStudentsCount,
      sortable: true,
      width: "120px",
      cell: (r) => (
        <div style={{ fontSize: 12 }}>
          <div style={{ fontWeight: 600 }}>{r.totalStudentsCount} total</div>
          <div style={{ color: "#94a3b8" }}>{r.absentStudents} absent</div>
        </div>
      ),
    },
    {
      id: "participants",
      name: "Participants",
      selector: (r) => r.totalParticipantStudents,
      sortable: true,
      width: "120px",
      cell: (r) => (
        <div style={{ fontSize: 12 }}>
          <div style={{ fontWeight: 600 }}>{r.totalParticipantStudents}</div>
          <div style={{ color: "#94a3b8" }}>
            {r.passStudentsCount} pass · {r.failStudentsCount} fail
          </div>
        </div>
      ),
    },
    // ── Rate metrics ──────────────────────────────────────────────────
    {
      id: "participationRate",
      name: "Participation",
      selector: (r) => r.participationRate,
      sortable: true,
      minWidth: "120px",
      cell: (r) => <MiniBar value={r.participationRate} />,
    },
    {
      id: "passRate",
      name: "Pass rate",
      selector: (r) => r.passRate,
      sortable: true,
      minWidth: "120px",
      cell: (r) => <MiniBar value={r.passRate} />,
    },
    {
      id: "avgScore",
      name: "Avg score",
      selector: (r) => r.studentsAveragePercentage,
      sortable: true,
      minWidth: "120px",
      cell: (r) => <MiniBar value={r.studentsAveragePercentage} />,
    },
    {
      id: "avgMarks",
      name: "Avg marks",
      selector: (r) => r.subjectAverageMarks,
      sortable: true,
      width: "100px",
      cell: (r) => (
        <span style={{ fontSize: 13, fontWeight: 500 }}>
          {Number(r.subjectAverageMarks).toFixed(2)}
        </span>
      ),
    },
    {
      id: "range",
      name: "Score range",
      sortable: false,
      minWidth: "130px",
      cell: (r) => (
        <ScoreRange
          best={r.studentsBestObtainPercentage}
          poorest={r.studentsPoorestObtainPercentage}
        />
      ),
    },
    // {
    //   id: "markRange",
    //   name: "Spread",
    //   selector: (r) => r.markRange,
    //   sortable: true,
    //   width: "80px",
    //   cell: (r) => (
    //     <span style={{ fontSize: 12, color: "#64748b" }}>
    //       ±{Number(r.markRange).toFixed(1)}%
    //     </span>
    //   ),
    // },
    // // ── Performance band ──────────────────────────────────────────────
    // {
    //   id: "band",
    //   name: "Band",
    //   selector: (r) => r.performanceBand,
    //   sortable: true,
    //   cell: (r) => <BandPill band={r.performanceBand} />,
    // },
  ];

  return all.filter((c) => !c.omit);
};



// ─── Custom styles for DataTable ──────────────────────────────────────────────
const tableStyles = {
  headCells: {
    style: {
      fontWeight: 700,
      fontSize: "0.75rem",
      textTransform: "uppercase",
      letterSpacing: "0.05em",
      color: "#64748b",
      background: "#f8fafc",
      paddingTop: 10,
      paddingBottom: 10,
    },
  },
  rows: {
    style: {
      fontSize: "0.875rem",
      "&:hover": { background: "#f8fafc" },
    },
  },
  cells: {
    style: { paddingTop: 8, paddingBottom: 8 },
  },
};

// ─── Main component ───────────────────────────────────────────────────────────
const ListAllTeachers = ({ teachers }) => {
  const [listTeachers, setListTeachers] = useState(teachers || []);
  const [loading, setLoading] = useState(false);
  const [groupBy, setGroupBy] = useState("DETAIL");
  const [permissionModal, setPermissionModal] = useState(false);

  const getAllTeachers = useCallback(() => {
    setLoading(true);
    axiosPost(URL.getPeopleForEvaluation, { groupBy }, (response) => {
      if (response.status === 200) {
        setListTeachers(response.data.data);
      }
      setLoading(false);
    });
  }, [groupBy]);

  useEffect(() => {
    getAllTeachers();
  }, [getAllTeachers]);

  const columns = buildColumns(groupBy);

  return (
    <div className="students-container" style={{ height: "80vh", overflow: "auto" }}>
      {/* ── Header ── */}
      <div className="header flex" style={{ marginBottom: 12 }}>
        <div className="flex header-btns" style={{ gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          {/* Group-by tabs */}
          <div
            style={{
              display: "flex",
              gap: 0,
              border: "1px solid #e2e8f0",
              borderRadius: 8,
              overflow: "hidden",
            }}
          >
            {TABS.map((tab) => (
              <button
                key={tab.value}
                onClick={() => setGroupBy(tab.value)}
                style={{
                  padding: "7px 14px",
                  fontSize: 13,
                  fontWeight: groupBy === tab.value ? 600 : 400,
                  background: groupBy === tab.value ? "#0f172a" : "#fff",
                  color: groupBy === tab.value ? "#fff" : "#64748b",
                  border: "none",
                  borderRight: "1px solid #e2e8f0",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  transition: "all .15s",
                }}
              >
                <FontAwesomeIcon icon={tab.icon} style={{ fontSize: 11 }} />
                {tab.label}
              </button>
            ))}
          </div>

          {checkWidgetAdminOrNot("Learning Achievement") && (
            <button
              className="primary-btn"
              onClick={() => setPermissionModal((prev) => !prev)}
            >
              Permission
            </button>
          )}
        </div>
      </div>

      {/* ── Table ── */}
      <div className="card-body" >
        <DataTable
          columns={columns}
          data={listTeachers}
          pagination
          paginationPerPage={10}
          paginationRowsPerPageOptions={[10, 20, 50]}
          progressPending={loading}
          progressComponent={
            <div style={{ padding: 32, color: "#94a3b8", fontSize: 13 }}>
              Loading...
            </div>
          }
          noDataComponent={
            <div style={{ padding: 32, color: "#94a3b8", fontSize: 13 }}>
              No records found for this grouping.
            </div>
          }
          customStyles={tableStyles}
          defaultSortFieldId="avgScore"
          defaultSortAsc={false}
          highlightOnHover
          striped
          responsive
          fixedHeader
        />
      </div>

      {/* ── Permission modal ── */}
      <ModalWindow
        modal={permissionModal}
        toggleModal={() => setPermissionModal((prev) => !prev)}
        modalHeader="Assign permission to user"
        size="lg"
        id="tt-permissionModal"
        modalBody={
          <GranularPermissionModal
            widgetName="Students"
            moduleName="Students"
            header="List Users"
            activityName="list-students"
          />
        }
      />
    </div>
  );
};

export default ListAllTeachers;